/**
 * Terminaux techniciens.
 *
 * Approuver ou révoquer est une décision humaine : l'écran affiche donc l'état
 * réel, y compris ce qui n'est PAS garanti — un terminal révoqué dont
 * l'effacement n'est pas confirmé est signalé comme tel.
 */
import { useCallback, useEffect, useState } from 'react';

import { ApiError, api, type Device } from '../api/client';

const LIBELLE_ETAT: Record<Device['status'], { texte: string; classe: string }> = {
  PENDING: { texte: 'En attente', classe: 'attente' },
  ACTIVE: { texte: 'Actif', classe: 'actif' },
  SUSPENDED: { texte: 'Suspendu', classe: 'suspendu' },
  REVOKED: { texte: 'Révoqué', classe: 'revoque' },
};

function dateCourte(valeur: string | null): string {
  if (!valeur) return '—';
  return new Date(valeur).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function DevicesPage() {
  const [terminaux, setTerminaux] = useState<Device[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);

  const charger = useCallback(async () => {
    try {
      setTerminaux(await api.devices());
      setErreur(null);
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Chargement impossible.');
    }
  }, []);

  useEffect(() => {
    void charger();
  }, [charger]);

  const approuver = async (terminal: Device) => {
    setEnCours(terminal.id);
    setInfo(null);
    try {
      await api.approveDevice(terminal.id);
      setInfo(`Terminal « ${terminal.label} » approuvé.`);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Approbation impossible.');
    } finally {
      setEnCours(null);
    }
  };

  const revoquer = async (terminal: Device) => {
    // Le motif est obligatoire côté API : le demander ici évite un aller-retour
    // pour rien, et force à formuler la raison au moment de la décision.
    const motif = window.prompt(
      `Révoquer « ${terminal.label} » ?\n\nCette action est irréversible et ferme immédiatement ses sessions.\nMotif :`,
    );
    if (motif === null) return;

    setEnCours(terminal.id);
    setInfo(null);
    try {
      await api.revokeDevice(terminal.id, motif);
      setInfo(`Terminal « ${terminal.label} » révoqué.`);
      await charger();
    } catch (e) {
      setErreur(e instanceof ApiError ? e.message : 'Révocation impossible.');
    } finally {
      setEnCours(null);
    }
  };

  return (
    <>
      <div className="entete-page">
        <div>
          <h1>Terminaux</h1>
          <p>Tablettes et téléphones des techniciens. Un terminal doit être approuvé avant usage.</p>
        </div>
        <button className="secondaire" onClick={() => void charger()}>
          Actualiser
        </button>
      </div>

      {erreur && <div className="message erreur">{erreur}</div>}
      {info && <div className="message succes">{info}</div>}

      <div className="carte">
        {terminaux === null ? (
          <div className="vide">Chargement…</div>
        ) : terminaux.length === 0 ? (
          <div className="vide">
            Aucun terminal enrôlé.
            <br />
            Un technicien enrôle son appareil depuis l’application terrain ; il apparaîtra ici pour
            approbation.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Terminal</th>
                <th>Plateforme</th>
                <th>État</th>
                <th>Dernière activité</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {terminaux.map((t) => {
                const etat = LIBELLE_ETAT[t.status];
                const occupe = enCours === t.id;
                return (
                  <tr key={t.id}>
                    <td>
                      <strong>{t.label}</strong>
                      {t.status === 'REVOKED' && t.wipedAt === null && (
                        <div className="aide" style={{ color: 'var(--orange)' }}>
                          Effacement des données non confirmé par l’appareil
                        </div>
                      )}
                      {t.revokedReason && <div className="aide">Motif : {t.revokedReason}</div>}
                    </td>
                    <td>
                      {t.platform ?? '—'}
                      {t.appVersion && <div className="aide">version {t.appVersion}</div>}
                    </td>
                    <td>
                      <span className={`etat ${etat.classe}`}>{etat.texte}</span>
                    </td>
                    <td>{dateCourte(t.lastSeenAt)}</td>
                    <td>
                      <div className="actions">
                        {(t.status === 'PENDING' || t.status === 'SUSPENDED') && (
                          <button className="discret" disabled={occupe} onClick={() => void approuver(t)}>
                            Approuver
                          </button>
                        )}
                        {t.status !== 'REVOKED' && (
                          <button
                            className="discret danger"
                            disabled={occupe}
                            onClick={() => void revoquer(t)}
                          >
                            Révoquer
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
